import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const { register, loginWithGoogle, loading, error } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const validatePassword = () => {
    if (password !== confirmPassword) {
      setPasswordError(t('auth.register.passwordsDoNotMatch'));
      return false;
    }
    if (password.length < 6) {
      setPasswordError(t('auth.register.passwordTooShort'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;
    try {
      await register(email, password, name);
      navigate('/');
    } catch {}
  };

  const handleGoogleRegister = async () => {
    try {
      await loginWithGoogle();
      navigate('/');
    } catch {}
  };

  const field = (
    icon: string,
    id: string,
    label: string,
    type: string,
    value: string,
    onChange: (v: string) => void,
    placeholder?: string,
    extra?: React.ReactNode
  ) => (
    <div className="space-y-2">
      <label className="font-headline text-label-md text-on-surface-variant" htmlFor={id}>{label}</label>
      <div className="relative flex items-center">
        <span className="material-symbols-outlined absolute left-4 text-on-surface-variant">{icon}</span>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          className="w-full pl-12 pr-4 py-3 bg-surface border border-surface-container-highest rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all font-body text-body-md"
        />
        {extra}
      </div>
    </div>
  );

  return (
    <div className="flex-grow flex flex-col bg-background">
      <div className="flex-grow flex items-center justify-center p-4 md:p-margin-desktop">
        <div className="w-full max-w-[440px] bg-surface-container-lowest rounded-xl card-elevation p-8 md:p-10 flex flex-col items-center">

          <img src="/logo.png" alt="Meettix" className="w-20 h-20 object-contain mb-2" />
          <h1 className="font-headline text-headline-md text-center text-on-surface mb-8">
            {t('auth.register.title')}
          </h1>

          {error && (
            <div className="w-full mb-6 bg-error-container text-on-error-container px-4 py-3 rounded-lg text-body-md">
              {error}
            </div>
          )}

          <form className="w-full space-y-5" onSubmit={handleSubmit}>
            {field('person', 'name', t('auth.register.name'), 'text', name, setName, 'Seu nome')}
            {field('mail', 'email', t('auth.register.email'), 'email', email, setEmail, 'nome@exemplo.com')}

            <div className="space-y-2">
              <label className="font-headline text-label-md text-on-surface-variant" htmlFor="password">
                {t('auth.register.password')}
              </label>
              <div className="relative flex items-center">
                <span className="material-symbols-outlined absolute left-4 text-on-surface-variant">lock</span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-12 pr-12 py-3 bg-surface border border-surface-container-highest rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all font-body text-body-md"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 text-on-surface-variant hover:text-primary transition-colors">
                  <span className="material-symbols-outlined">{showPassword ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="font-headline text-label-md text-on-surface-variant" htmlFor="confirmPassword">
                {t('auth.register.confirmPassword')}
              </label>
              <div className="relative flex items-center">
                <span className="material-symbols-outlined absolute left-4 text-on-surface-variant">lock</span>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-surface border border-surface-container-highest rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all font-body text-body-md"
                  placeholder="••••••••"
                />
              </div>
              {passwordError && <p className="font-body text-label-sm text-error">{passwordError}</p>}
            </div>

            <div className="flex items-center gap-3">
              <input id="terms" type="checkbox" required className="h-4 w-4 accent-primary rounded" />
              <label htmlFor="terms" className="font-body text-body-md text-on-surface-variant">
                {t('auth.register.terms')}
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-container text-on-primary-container py-4 rounded-lg font-headline text-label-md hover:brightness-110 transition-all active:scale-95 uppercase tracking-wide disabled:opacity-50"
            >
              {loading ? t('auth.register.creatingAccount') : t('auth.register.createAccount')}
            </button>
          </form>

          <div className="w-full flex items-center my-8">
            <div className="flex-grow border-t border-outline-variant" />
            <span className="px-4 font-headline text-label-sm text-on-surface-variant">ou</span>
            <div className="flex-grow border-t border-outline-variant" />
          </div>

          <button
            onClick={handleGoogleRegister}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-surface-container-lowest border border-outline text-on-surface py-3 rounded-lg font-headline text-label-md hover:bg-surface-container transition-colors active:scale-95 disabled:opacity-50"
          >
            <svg height="20" viewBox="0 0 24 24" width="20">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {t('auth.register.google')}
          </button>

          <div className="mt-8 text-center">
            <p className="font-body text-body-md text-on-surface-variant">
              {t('auth.register.haveAccount')}{' '}
              <Link to="/login" className="text-primary font-bold hover:underline">
                {t('nav.login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
